/**
 * datalayer-gtm.js
 * GA4 Enhanced Ecommerce Events - JavaScript extracted from datalayer-gtm.liquid
 * Note: Initial dataLayer pushes for view_item and view_item_list are done inline
 * in datalayer-gtm.liquid because they require Liquid template data.
 */

(function() {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  /**
   * Format price from cents to currency units
   * @param {number} price - Price in cents
   * @returns {number} Price in currency units
   */
  function formatPrice(price) {
    return parseFloat(price) / 100;
  }

  /**
   * Initialize all GTM event listeners
   * @param {object} config - Configuration object with currency and collection info
   */
  function initGTMTracking(config) {
    const currency = config.currency || 'EUR';
    const defaultListName = config.defaultListName || 'Product List';

    // ADD_TO_CART - Listen for cart updates
    document.addEventListener('cart:update', function(event) {
      const detail = event.detail;
      if (!detail || !detail.productId) return;

      const productData = window.productDataForGTM || {};

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currency,
          value: productData.price || 0,
          items: [{
            item_id: detail.productId,
            item_name: productData.title || '',
            item_variant: productData.variant || '',
            price: productData.price || 0,
            quantity: detail.itemCount || 1
          }]
        }
      });
    });

    // SELECT_ITEM - Click on product card
    document.addEventListener('click', function(event) {
      const productCard = event.target.closest('[data-product-card], .product-card, product-card');
      if (!productCard) return;

      const link = productCard.querySelector('a[href*="/products/"]');
      if (!link) return;

      const productId = productCard.dataset.productId || '';
      const productTitle = productCard.querySelector('[data-product-title], .product-card__title')?.textContent?.trim() || '';
      const priceElement = productCard.querySelector('[data-product-price], .price__value, .price .number');
      const productPrice = priceElement ? parseFloat(priceElement.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) : 0;
      const listName = document.querySelector('[data-collection-title]')?.textContent?.trim() || defaultListName;

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          currency: currency,
          item_list_name: listName,
          items: [{
            item_id: productId,
            item_name: productTitle,
            price: productPrice,
            quantity: 1
          }]
        }
      });
    });

    // VARIANT_SELECTED - Track option/variant selection
    document.addEventListener('change', function(event) {
      const target = event.target;
      const variantPicker = target.closest('variant-picker, swatches-variant-picker-component');
      if (!variantPicker) return;

      const isRadio = target.type === 'radio';
      const isSelect = target.tagName === 'SELECT';
      if (!isRadio && !isSelect) return;

      const optionName = isRadio
        ? target.closest('fieldset')?.querySelector('legend')?.textContent?.trim()
        : target.closest('.variant-option')?.querySelector('label')?.textContent?.trim();

      const optionValue = target.value;
      const variantId = target.dataset.variantId || '';
      const optionValueId = target.dataset.optionValueId || '';
      const isAvailable = target.dataset.optionAvailable !== 'false';

      window.dataLayer.push({
        event: 'variant_selected',
        product_id: window.productDataForGTM?.id || '',
        product_name: window.productDataForGTM?.title || '',
        option_name: optionName || '',
        option_value: optionValue,
        variant_id: variantId,
        option_value_id: optionValueId,
        option_available: isAvailable
      });
    });

    // QUANTITY_CHANGED - Track quantity adjustments (enhanced for cart items)
    document.addEventListener('click', function(event) {
      const button = event.target.closest('quantity-selector-component button');
      if (!button) return;

      const container = button.closest('quantity-selector-component');
      const input = container?.querySelector('input[type="number"]');
      if (!input) return;

      const action = button.name === 'plus' ? 'increase' : 'decrease';
      const currentValue = parseInt(input.value) || 1;
      const newValue = action === 'increase' ? currentValue + 1 : Math.max(1, currentValue - 1);

      // Determine section name based on context
      let sectionName = 'product_page';
      let productId = window.productDataForGTM?.id || '';
      let productName = window.productDataForGTM?.title || '';
      let variantTitle = '';
      let productPrice = 0;

      // Check if we're in a cart context and get cart item data
      const cartRow = container.closest('tr[data-key], .cart-item, [data-cart-item]');
      if (cartRow) {
        // Get product info from cart row
        const titleElement = cartRow.querySelector('.cart-items__title, .cart-item__title, [data-product-title]');
        productName = titleElement?.textContent?.trim() || productName;

        const variantElement = cartRow.querySelector('.cart-items__variant dd, .cart-item__variant');
        variantTitle = variantElement?.textContent?.trim() || '';

        const priceElement = cartRow.querySelector('.cart-items__price text-component, .cart-item__price, [data-line-price]');
        const priceText = priceElement?.textContent?.trim() || '';
        productPrice = parseFloat(priceText.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;

        // Get product key for identification
        productId = cartRow.dataset?.key || productId;
      }

      if (container.closest('cart-drawer, .cart-drawer')) sectionName = 'cart_drawer';
      else if (container.closest('cart-items, .cart-items, [data-cart-items]')) sectionName = 'cart_page';
      else if (container.closest('.quick-add, quick-add')) sectionName = 'quick_add';

      window.dataLayer.push({
        event: 'quantity_changed',
        product_id: productId,
        product_name: productName,
        variant_title: variantTitle,
        product_price: productPrice,
        action: action,
        button_type: button.name, // 'plus' or 'minus'
        old_quantity: currentValue,
        new_quantity: newValue,
        section_name: sectionName
      });
    });

    // BUNDLE_SELECTED - Track bundle option selection
    document.addEventListener('change', function(event) {
      const target = event.target;
      const bundleSelector = target.closest('bundle-selector-component');
      if (!bundleSelector) return;

      const bundleQuantity = target.dataset.bundleQuantity || target.value;
      const bundleCard = target.closest('.bundle-card, .bundle-option');
      const bundleLabel = bundleCard?.querySelector('label, .bundle-label')?.textContent?.trim() || '';

      window.dataLayer.push({
        event: 'bundle_selected',
        product_id: window.productDataForGTM?.id || '',
        product_name: window.productDataForGTM?.title || '',
        bundle_quantity: bundleQuantity,
        bundle_label: bundleLabel
      });
    });

    // TEEINBLUE_FIELD_INTERACTION - Track custom field changes
    document.addEventListener('change', function(event) {
      const target = event.target;
      const teeForm = target.closest('#teeFormInputs, .tee-form, [class*="teeinblue"]');
      if (!teeForm) return;

      const fieldContainer = target.closest('.tee-field, [class*="tee-"]');
      const fieldLabel = fieldContainer?.querySelector('label, .tee-field__label')?.textContent?.trim()?.replace('*', '').trim() || '';
      const fieldValue = target.type === 'radio' || target.type === 'checkbox'
        ? target.value
        : (target.value ? 'filled' : 'empty');

      window.dataLayer.push({
        event: 'custom_field_interaction',
        product_id: window.productDataForGTM?.id || '',
        product_name: window.productDataForGTM?.title || '',
        field_name: fieldLabel,
        field_type: target.type || 'unknown',
        field_value: fieldValue
      });
    });

    // MENU_CLICK - Track menu navigation usage
    document.addEventListener('click', function(event) {
      // Track menu drawer link clicks
      const menuLink = event.target.closest('.menu-drawer__menu-item, .menu__item a, header-menu a, .header-menu a');
      if (!menuLink) return;

      // Don't track summary elements (accordion toggles)
      if (menuLink.tagName === 'SUMMARY') return;

      const linkText = menuLink.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim()
        || menuLink.textContent?.trim()
        || '';
      const linkUrl = menuLink.href || '';

      // Determine menu level
      let menuLevel = 'top';
      if (menuLink.closest('.menu-drawer__menu--grandchildlist')) {
        menuLevel = 'grandchild';
      } else if (menuLink.closest('.menu-drawer__menu--childlist')) {
        menuLevel = 'child';
      } else if (menuLink.classList.contains('menu-drawer__menu-item--mainlist')) {
        menuLevel = 'main';
      }

      // Determine menu type
      let menuType = 'unknown';
      if (menuLink.closest('header-drawer, .header-drawer')) {
        menuType = 'mobile_drawer';
      } else if (menuLink.closest('header-menu, .header-menu')) {
        menuType = 'desktop_menu';
      } else if (menuLink.closest('.menu, accordion-custom')) {
        menuType = 'footer_menu';
      }

      window.dataLayer.push({
        event: 'menu_click',
        menu_type: menuType,
        menu_level: menuLevel,
        link_text: linkText,
        link_url: linkUrl,
        page_url: window.location.href
      });
    });

    // COLLECTIONS_SLIDER_CLICK - Track collections slider interactions
    document.addEventListener('click', function(event) {
      const sliderLink = event.target.closest('.collections-slider a, .an-cus-product-slider a');
      if (!sliderLink) return;

      const sliderContainer = sliderLink.closest('.collections-slider, .old-collections-section');
      const slideWrapper = sliderLink.closest('.an-cus-product-slide-wraper');

      // Get collection title
      const titleElement = slideWrapper?.querySelector('.collection-card-title h2, .collection-card-title');
      const collectionTitle = titleElement?.textContent?.trim() || '';

      // Get section heading
      const sectionHeading = sliderContainer?.querySelector('h1, h2, h3, h4')?.textContent?.trim() || '';

      // Get slide position (index)
      let slidePosition = 0;
      if (slideWrapper && sliderContainer) {
        const allSlides = sliderContainer.querySelectorAll('.an-cus-product-slide-wraper');
        slidePosition = Array.from(allSlides).indexOf(slideWrapper) + 1;
      }

      const linkUrl = sliderLink.href || '';

      window.dataLayer.push({
        event: 'collections_slider_click',
        collection_title: collectionTitle,
        section_heading: sectionHeading,
        slide_position: slidePosition,
        link_url: linkUrl,
        page_url: window.location.href
      });
    });

    // FIELD FOCUS TRACKING - Track last focused/filled field
    let lastFocusedField = {
      name: '',
      type: '',
      section: '',
      value: ''
    };

    function getFieldInfo(element) {
      let fieldName = '';
      let fieldSection = '';

      // Check for variant picker fields
      const variantPicker = element.closest('variant-picker, swatches-variant-picker-component');
      if (variantPicker) {
        const fieldset = element.closest('fieldset');
        fieldName = fieldset?.querySelector('legend')?.textContent?.trim() || element.name || 'variant_option';
        fieldSection = 'variant_picker';
        return { name: fieldName, section: fieldSection };
      }

      // Check for bundle selector
      const bundleSelector = element.closest('bundle-selector-component');
      if (bundleSelector) {
        fieldName = 'bundle_quantity';
        fieldSection = 'bundle_selector';
        return { name: fieldName, section: fieldSection };
      }

      // Check for quantity selector
      const quantitySelector = element.closest('quantity-selector-component');
      if (quantitySelector) {
        fieldName = 'quantity';
        fieldSection = 'quantity_selector';
        return { name: fieldName, section: fieldSection };
      }

      // Check for TeeInBlue fields
      const teeForm = element.closest('#teeFormInputs, .tee-form, [class*="teeinblue"]');
      if (teeForm) {
        const fieldContainer = element.closest('.tee-field, [class*="tee-"]');
        fieldName = fieldContainer?.querySelector('label, .tee-field__label')?.textContent?.trim()?.replace('*', '').trim() || element.name || 'custom_field';
        fieldSection = 'customization_form';
        return { name: fieldName, section: fieldSection };
      }

      // Check for add to cart button
      const addToCart = element.closest('add-to-cart-component');
      if (addToCart) {
        fieldName = 'add_to_cart_button';
        fieldSection = 'add_to_cart';
        return { name: fieldName, section: fieldSection };
      }

      // Generic form field
      const label = element.closest('label') || document.querySelector('label[for="' + element.id + '"]');
      fieldName = label?.textContent?.trim() || element.name || element.placeholder || 'unknown_field';
      fieldSection = 'other';

      return { name: fieldName, section: fieldSection };
    }

    // Track focus events on form fields
    document.addEventListener('focusin', function(event) {
      const target = event.target;
      if (!target.matches('input, select, textarea, button, [role="radio"], [role="checkbox"]')) return;

      const fieldInfo = getFieldInfo(target);
      lastFocusedField = {
        name: fieldInfo.name,
        type: target.type || target.tagName.toLowerCase(),
        section: fieldInfo.section,
        value: target.value || ''
      };

      window.dataLayer.push({
        event: 'form_field_focus',
        product_id: window.productDataForGTM?.id || '',
        product_name: window.productDataForGTM?.title || '',
        field_name: lastFocusedField.name,
        field_type: lastFocusedField.type,
        field_section: lastFocusedField.section
      });
    });

    // Track blur events to capture filled values
    document.addEventListener('focusout', function(event) {
      const target = event.target;
      if (!target.matches('input, select, textarea')) return;

      const fieldInfo = getFieldInfo(target);
      const fieldValue = target.type === 'radio' || target.type === 'checkbox'
        ? (target.checked ? target.value : '')
        : target.value;

      lastFocusedField = {
        name: fieldInfo.name,
        type: target.type || target.tagName.toLowerCase(),
        section: fieldInfo.section,
        value: fieldValue ? 'filled' : 'empty'
      };
    });

    // FORM_STEP_PROGRESS - Track scroll/visibility of form sections
    if ('IntersectionObserver' in window) {
      const formSections = document.querySelectorAll('variant-picker, quantity-selector-component, bundle-selector-component, #teeFormInputs, .tee-form, add-to-cart-component');
      const observedSections = new Set();

      const sectionObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && !observedSections.has(entry.target)) {
            observedSections.add(entry.target);

            let sectionName = 'unknown';
            if (entry.target.matches('variant-picker')) sectionName = 'variant_picker';
            else if (entry.target.matches('quantity-selector-component')) sectionName = 'quantity_selector';
            else if (entry.target.matches('bundle-selector-component')) sectionName = 'bundle_selector';
            else if (entry.target.matches('#teeFormInputs, .tee-form')) sectionName = 'customization_form';
            else if (entry.target.matches('add-to-cart-component')) sectionName = 'add_to_cart_button';

            // Get current quantity if quantity selector is visible
            const quantityInput = document.querySelector('quantity-selector-component input[type="number"]');
            const currentQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

            window.dataLayer.push({
              event: 'form_section_viewed',
              product_id: window.productDataForGTM?.id || '',
              product_name: window.productDataForGTM?.title || '',
              section_name: sectionName,
              last_field_name: lastFocusedField.name,
              last_field_section: lastFocusedField.section,
              last_field_value: lastFocusedField.value,
              current_quantity: currentQuantity
            });
          }
        });
      }, { threshold: 0.5 });

      formSections.forEach(function(section) {
        sectionObserver.observe(section);
      });
    }

    // ADD_TO_CART_ATTEMPT - Track when user tries to add (with validation status)
    document.addEventListener('submit', function(event) {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;

      const validationErrors = [];
      const fieldStatus = {};

      // Check TeeInBlue required fields
      const teeForm = document.querySelector('#teeFormInputs, .tee-form, [class*="teeinblue"]');
      if (teeForm) {
        const requiredFields = teeForm.querySelectorAll('.tee-field:has(.tee-asterisk), [class*="tee-"]:has([aria-required="true"])');

        requiredFields.forEach(function(field) {
          const fieldLabel = field.querySelector('label, .tee-field__label')?.textContent?.trim()?.replace('*', '').trim() || 'Unknown field';
          const fieldInput = field.querySelector('.tee-field__input') || field;

          let isValid = true;
          let errorType = '';

          const radioGroup = fieldInput.querySelector('[role="radiogroup"]');
          if (radioGroup) {
            const checkedRadio = radioGroup.querySelector('input[type="radio"]:checked');
            if (!checkedRadio) {
              isValid = false;
              errorType = 'no_selection';
            }
          } else {
            const input = fieldInput.querySelector('input[aria-required="true"], textarea[aria-required="true"], input:required, textarea:required');
            if (input) {
              const value = input.value?.trim() || '';
              if (value === '') {
                isValid = false;
                errorType = 'empty';
              }
            }
          }

          fieldStatus[fieldLabel] = isValid ? 'valid' : 'invalid';
          if (!isValid) {
            validationErrors.push({
              field: fieldLabel,
              error: errorType
            });
          }
        });
      }

      // Check variant selection
      if (!variantId) {
        validationErrors.push({ field: 'variant', error: 'no_selection' });
        fieldStatus['variant'] = 'invalid';
      } else {
        fieldStatus['variant'] = 'valid';
      }

      // Check if variant is available
      const selectedVariantAvailable = document.querySelector('variant-picker input:checked')?.dataset?.optionAvailable;
      if (selectedVariantAvailable === 'false') {
        validationErrors.push({ field: 'variant', error: 'unavailable' });
        fieldStatus['variant'] = 'unavailable';
      }

      window.dataLayer.push({
        event: 'add_to_cart_attempt',
        product_id: window.productDataForGTM?.id || '',
        product_name: window.productDataForGTM?.title || '',
        variant_id: variantId,
        quantity: parseInt(quantity),
        variant_title: window.productDataForGTM?.variant || '',
        validation_passed: validationErrors.length === 0,
        validation_errors: validationErrors,
        validation_error_count: validationErrors.length,
        field_status: fieldStatus
      });

      if (validationErrors.length > 0) {
        window.dataLayer.push({
          event: 'form_validation_error',
          product_id: window.productDataForGTM?.id || '',
          product_name: window.productDataForGTM?.title || '',
          error_fields: validationErrors.map(e => e.field),
          error_details: validationErrors
        });
      }
    });

    // Update product data when variant changes
    document.addEventListener('variant:update', function(event) {
      const variant = event.detail?.resource;
      if (!variant || !window.productDataForGTM) return;

      window.productDataForGTM.variant = variant.title || '';
      window.productDataForGTM.price = variant.price ? variant.price / 100 : window.productDataForGTM.price;
    });
  }

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    const config = window.gtmConfig || {};
    initGTMTracking(config);
  });

  // Export for external use
  window.PivvyGTM = {
    formatPrice: formatPrice,
    init: initGTMTracking
  };
})();
